import { INotificationOptions, notification } from '@pu/mpp-ui/notifications';
import { TAlertAnimation } from 'shared/components/alert/alert.types';
import { IDrawerAlertHandlers } from '../components/drawer/drawer.types';

export type THandlersGlobalNotification = keyof typeof notification;
export type THandlersDrawerNotification = keyof IDrawerAlertHandlers;

export interface IDrawerNotification {
  handler: THandlersDrawerNotification;
  message: string;
  autoHide?: boolean;
  autoHideDuration?: number;
  animation?: TAlertAnimation;
}

export interface IGlobalNotification {
  handler: THandlersGlobalNotification;
  message?: string;
  options?: INotificationOptions;
}

let drawerCallback: ((notification: IDrawerNotification) => void) | null = null;
let globalCallback: ((notification: IGlobalNotification) => void) | null = null;

const addDrawerCallback = (callback: (notification: IDrawerNotification) => void) => {
  drawerCallback = callback;
  return () => {
    drawerCallback = null;
  };
};

const addGlobalCallback = (callback: (notification: IGlobalNotification) => void) => {
  globalCallback = callback;
  return () => {
    globalCallback = null;
  };
};

const showDrawerNotification = (notification: IDrawerNotification): void => {
  if (drawerCallback) drawerCallback(notification);
};

const showGlobalNotification = (notification: IGlobalNotification): void => {
  if (globalCallback) globalCallback(notification);
};

const createDrawerNotification = (
  handler: THandlersDrawerNotification,
  message: string,
  options: Omit<IDrawerNotification, 'handler' | 'message'> = {},
): IDrawerNotification => ({
  handler,
  message,
  ...options,
});

const createGlobalNotification = (
  handler: THandlersGlobalNotification,
  message: string,
  options?: INotificationOptions,
): IGlobalNotification => ({
  handler,
  message,
  options,
});

export const drawerNotifications = {
  success: (message: string, options: Omit<IDrawerNotification, 'handler' | 'message'> = {}) =>
    showDrawerNotification(createDrawerNotification('success', message, options)),

  error: (message: string, options: Omit<IDrawerNotification, 'handler' | 'message'> = {}) =>
    showDrawerNotification(createDrawerNotification('error', message, options)),

  warning: (message: string, options: Omit<IDrawerNotification, 'handler' | 'message'> = {}) =>
    showDrawerNotification(createDrawerNotification('warning', message, options)),

  clear: () => showDrawerNotification({ handler: 'clear', message: '' }),

  isAvailable: () => !!drawerCallback,
};

export const globalNotifications = {
  success: (message: string, options?: INotificationOptions) =>
    showGlobalNotification(createGlobalNotification('success', message, options)),

  error: (message: string, options?: INotificationOptions) =>
    showGlobalNotification(createGlobalNotification('error', message, options)),

  warning: (message: string, options?: INotificationOptions) =>
    showGlobalNotification(createGlobalNotification('warning', message, options)),

  clear: () => showGlobalNotification({ handler: 'clear' }),
};

export const notificationService = {
  drawer: drawerNotifications,
  global: globalNotifications,
  registerDrawerCallback: addDrawerCallback,
  registerGlobalCallback: addGlobalCallback,
};
